<template>
    <div>
        <template v-if="loading">
            <div class="loading-container">
                <p>{{ $t('surveys.loading') }}</p>
            </div>
        </template>

        <template v-if="isSurveyAccessible">
            <SurveyTemplate
                :surveyTitle="surveyTitle"
                :surveyId="surveyId"
                :items="surveyItems"
                :fromDialog="fromDialog"
                @surveyCompleted="handleSurveyCompleted"
								@surveyStepChanged="handleSurveyStepChanged"
								@surveyStarted="handleSurveyStarted"
								@surveyLoaded="handleSurveyLoaded"
            />
        </template>

        <template v-if="!isSurveyAccessible && !loading">
            <div class="survey-unavailable">
                <h3>{{ $i18n.t('surveys.not-avaliable') }}</h3>
                <p>{{ $i18n.t('surveys.not-allowed') }}</p>
            </div>
        </template>
    </div>
</template>

<script>
import SurveyRepository from '@/repositories/SurveyRepository'
import SurveyTemplate from './SurveyTemplate.vue'
import AccountRepository from '@/repositories/AccountRepository'
import i18nMixin from '@/mixins/i18n.mixin'
import LoadingAnim from '@/components/utils/LoadingAnim.vue'

const I18N_FILE = 'public/views/survey'

const EVENTS = {
	SURVEY_COMPLETED: 'surveyCompleted',
	SURVEY_STEP_CHANGED: 'surveyStepChanged',
	SURVEY_STARTED: 'surveyStarted'
}

export default {
    mixins: [i18nMixin(I18N_FILE)],
    components: {
        SurveyTemplate,
				LoadingAnim
    },
    props: {
        surveyIdProp: {
            type: String,
            default: null
        }
    },
    data () {
        return {
            surveyId: null,
            surveyTitle: '',
            surveyItems: [],
            surveyData: null,
            userBusinessCodes: [],
            userGroupCodes: [],
            loading: true,
            fromDialog: false
        }
    },
    computed: {
        userIsPatient () {
            return this.$store.getters['session/userIsPatient']
        },
        userIsCenter () {
            return this.$store.getters['session/userIsCenter']
        },
        userIsProfessional () {
            return this.$store.getters['session/userIsProfessional']
        },
        canAccessRole () {
            if (!this.surveyData) return false
            return (
                (this.surveyData.data.patient && this.userIsPatient) ||
                (this.surveyData.data.professional && this.userIsProfessional) ||
                (this.surveyData.data.center && this.userIsCenter)
            )
        },
        hasMatchingBusinessCode () {
            if (!this.surveyData) return false
            if (this.surveyData.data.forBusiness) {
                return this.userBusinessCodes.length > 0
            } else {
                return (
                    this.surveyData.data.businessCodeList.length === 0 ||
                    this.surveyData.data.businessCodeList.some(code => this.userBusinessCodes.includes(code))
                )
            }
        },
        hasMatchingGroupCode () {
            if (!this.surveyData) return false
            return (
                this.surveyData.data.codeGroupList.length === 0 ||
                this.surveyData.data.codeGroupList.some(code => this.userGroupCodes.includes(code))
            )
        },
        isSurveyAccessible () {
            return this.canAccessRole && this.hasMatchingBusinessCode && this.hasMatchingGroupCode
        }
    },
    watch: {
        surveyIdProp (newSurveyId) {
            if (newSurveyId) {
                this.surveyId = newSurveyId
                this.fetchSurveyData()
            }
        }
    },
    async mounted () {
        this.surveyId = this.surveyIdProp || this.$route.params.surveyId
        this.fromDialog = this.surveyIdProp != null

        if (!this.surveyId) {
            this.$router.push('/patient')
            return
        }

        await this.fetchSurveyData()
    },
    methods: {
        handleSurveyCompleted () {
            this.$emit(EVENTS.SURVEY_COMPLETED)
        },
				handleSurveyStepChanged (stepIndex) {
						this.$emit(EVENTS.SURVEY_STEP_CHANGED, stepIndex)
				},
				handleSurveyStarted () {
					this.$emit(EVENTS.SURVEY_STARTED)
				},
				handleSurveyLoaded () {
					this.loading = false
				},
        async fetchSurveyData () {
            try {
                // Obtener sesión antes de continuar
                const loginInfoResponse = await AccountRepository.getSession()
                this.$store.dispatch('session/set', loginInfoResponse.data)

                // Cargar datos en paralelo
                const [surveyResponse, businessCodesResponse, groupCodesResponse] = await Promise.all([
                    SurveyRepository.getAllItemsBySurvey(this.surveyId),
                    SurveyRepository.checkBusinessCodesByUser(),
                    SurveyRepository.checkGroupCodesByUser()
                ])

                this.surveyData = surveyResponse
                this.userBusinessCodes = businessCodesResponse.data || []
                this.userGroupCodes = groupCodesResponse.data || []
                this.$store.commit('survey/setSurvey', this.surveyData.data.surveyName)

                if (this.isSurveyAccessible) {
                    this.surveyItems = this.surveyData.data.items
                    this.surveyTitle = this.surveyData.data.surveyName
                }
            } catch (error) {
                this.$store.dispatch('showDangerAlert', this.$i18n.t('surveys.error.error-loading'))
            }
        }
    }
}
</script>

<style scoped>
.loading-container {
    text-align: center;
    margin-top: 50px;
    font-size: 1.2rem;
    font-weight: bold;
}

.survey-unavailable {
    text-align: center;
    margin-top: 50px;
}
</style>



// WEBPACK FOOTER //
// src/components/views/surveys/SurveyTest.vue